import { Component, inject, OnInit, ViewContainerRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';

import { Format } from '@celum/formats/domain';
import { FORMAT_NAVIGATOR_SERVICE, FORMATS_CONTEXT, FormatStore } from '@celum/formats/shared';

import { FormatViewDialogComponent } from '../format-view/format-view-dialog.component';

/**
 * Single implementation used from the standalone `formats` app as well as both `formats-embed` routes (my
 * organization / administration). The concrete tenant/auth context (and, transitively, whether the dialog should
 * open in "admin view") is resolved from `FORMATS_CONTEXT`, provided by each app/route, so this component doesn't
 * need to be subclassed per context. Only `FORMATS_CONTEXT` is injected directly (rather than extending
 * `FormatsContextAware`) so this component isn't coupled to `AdminService`/`AuthService`/`TenantService`, which it
 * never actually needs.
 */
@Component({
  selector: 'dialog-launcher',
  template: ''
})
export class DialogLauncherComponent implements OnInit {
  private context = inject(FORMATS_CONTEXT);
  private activatedRoute = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private viewContainerRef = inject(ViewContainerRef);
  private navigator = inject(FORMAT_NAVIGATOR_SERVICE);
  private formatStore = inject(FormatStore);

  public ngOnInit(): void {
    this.openDialog();
  }

  private openDialog(): void {
    const isAdminView = this.context === 'EMBED_ORGANIZATION_ADMINISTRATION' ? true : this.context === 'EMBED_MY_PROFILE' ? false : undefined;
    const id = +this.activatedRoute.snapshot.paramMap.get('id');

    this.dialog
      .open(FormatViewDialogComponent, {
        data: { id, title: 'FORMAT_VIEW_DIALOG.TITLE.EDIT', action: 'FORMAT_VIEW_DIALOG.BUTTON.SAVE', isAdminView },
        width: '100%',
        height: '100%',
        maxHeight: '100%',
        maxWidth: '100%',
        viewContainerRef: this.viewContainerRef
      })
      .afterClosed()
      .subscribe((result: Format) => {
        if (result) {
          const exists = this.formatStore.entityMap()[result.id];
          if (exists) {
            this.formatStore.updateFormat(result);
          } else {
            this.formatStore.addFormat(result);
          }
        }

        this.navigator.openListView();
      });
  }
}
